import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import {
  ConfirmationService,
  Dialog,
  MessageService,
  TabPanel,
  TabView,
  TabViewModule,
} from 'primeng';
import { Message } from '../../classes/Message';
import { AgreementDataAccessDTO, IAgreement } from '../../interfaces/agreement';
import { IAgreementForm } from '../../interfaces/agreement-form';
import { IAgreementPricingBanding } from '../../interfaces/agreement-pricing-banding';
import { IAgreementTermsPostcode } from '../../interfaces/agreement-terms-postcode';
import { IRateCard } from '../../interfaces/ratecard';
import {
  AgreementService,
  OfficeHoursService,
  OrganisationsService,
  OrganisationTaskTypesService,
  UserService,
} from '../../services';
import { InputHelper } from '../../utils/input.helper';
import { ApplyPremiumFlagEnum } from '../../enums/apply-premium-flag.enum';
import { SupplierSignupTypeEnum } from '../../enums/supplier-signup-type.enum';
import { Location } from '@angular/common';
import { deepClone } from 'fast-json-patch';
import { CanComponentDeactivate } from 'src/app/guards/pending-changes.guard';
import { Observable } from 'rxjs';
import { IRateCardInclusivityType } from '../../interfaces/ratecard-inclusivity-type';

export enum AgreementType {
  GenericCustomerTerms = 1,
  GenericSupplierTerms = 2,
  GenericCustomer = 3,
  GenericSupplier = 4,
  BespokeCustomer = 5,
  BespokeSupplier = 6,
  PreferredSupplier = 7,
}

const pricingHoursTypeNames = new Map<number, string>([
  [1, 'Standard'],
  [2, 'Out of hours'],
  [3, 'Urgency'],
  [4, 'Standard Postcode'],
  [5, 'Out of hours Postcode'],
  [6, 'Urgent Postcode'],
]);

@Component({
  selector: 'agreement',
  templateUrl: './agreement.component.html',
  styleUrls: ['./agreement.component.css'],
  providers: [AgreementService, InputHelper],
  encapsulation: ViewEncapsulation.None,
})
export class AgreementComponent implements OnInit, CanComponentDeactivate {
  public backTo: string;

  public readonly AccessNotShown = 0;
  public readonly AccessReadOnly = 1;
  public readonly AccessEditable = 2;
  public readonly AccessReadOnlyUntilAgreed = 3;

  public isBusy: boolean = false;
  public isLoaded: boolean = false;

  public showOfferedServicesOnly: boolean = false;

  @ViewChild('tabviewMain') tabviewMain: TabViewModule;

  public agreementType = AgreementType;

  //url params
  //private id: number;
  public reqAgreementType: AgreementType;
  public reqAppliedOrgId: number;
  public reqAppliedDeptId: number;
  public agreementTypeHeader: string;
  public serviceDefaultDesc: string;

  //--- agreement's data loaded from server
  public agreementData: IAgreement;
  public srvGridData: any[];
  public rateGridData: any[];
  public filteredRateGridData: any[];
  public srvGridChecked: boolean[] = [];
  public bandings: IAgreementPricingBanding[];

  public access: AgreementDataAccessDTO = {} as AgreementDataAccessDTO;

  //--- agreement form
  public agreementForm: UntypedFormGroup;
  public agreement: IAgreementForm;

  //--- bandings dialog
  public selectedBanding: IAgreementPricingBanding;
  public bandingDialogVisible: boolean = false;
  public dlgBanding: IAgreementPricingBanding = { id: 0, from: 0 };
  public dlgBandingIndex: number = -1;
  public orgTypeId: any;
  public orgName: string;
  public deptName: string;
  public supplierSignupType: SupplierSignupTypeEnum;
  premiumFlagsList: any[];

  // note: using signup type to check this now, retaining this until sure it won't be needed
  //private allowedTaskTypes: { [key: number]: boolean };

  @ViewChild('tabviewPostcodes') tabviewPostcodes: TabViewModule;

  @ViewChild('tabUKPostcodes') tabUKPostcodes: TabPanel;
  @ViewChild('tabLondonPostcodes') tabLondonPostcodes: TabPanel;

  @ViewChild('postcodeDialog') postcodeDialog: Dialog;

  public isNetworkAssuranceAdmin: boolean;

  public selectedRateCardPresetId?: number = undefined;
  selectedRatecardPresetName: string = undefined;
  private rateGridDataJson: string = null;
  isSaved = false;

  inclusivityTypes: IRateCardInclusivityType[] = [];

  constructor(
    private route: ActivatedRoute,
    private agreementService: AgreementService,
    public officeHoursService: OfficeHoursService,
    public messageService: MessageService,
    public inputHelper: InputHelper,
    private organisationService: OrganisationsService,
    private userService: UserService,
  ) {}

  confirm(): boolean | Observable<boolean> {
    // note: this should be checking if there are actual changes before showing 'leave' confirmation
    return this.isSaved;
  }

  async ngOnInit() {
    this.isSaved = false;
    this.isNetworkAssuranceAdmin = await this.userService.isUserInRole('NADM');

    this.backTo = this.route.snapshot.params['backTo'];

    this.premiumFlagsList = Object.keys(ApplyPremiumFlagEnum)
      .filter((k) => isNaN(Number(k)))
      .map((k) => {
        const label = k
          .match(/([A-Z]?[^A-Z]*)/g)
          .slice(0, -1)
          .join(' ');
        return { label, value: ApplyPremiumFlagEnum[k] };
      });

    this.loadRateCardInclusivityTypes();

    this.premiumFlagsList = this.premiumFlagsList.filter((x) => x.value > 0);
    this.route.params.subscribe((params: Params) => {
      this.organisationService.getCurrentUserOrgTypeId().then((res) => {
        this.orgTypeId = res;
        console.log('Current user org type: ', this.orgTypeId);
        switch (params['type']) {
          case 'gc':
            this.reqAgreementType = AgreementType.GenericCustomerTerms;
            break;
          case 'gs':
            this.reqAgreementType = AgreementType.GenericSupplierTerms;
            break;
          case 'bc':
            this.reqAgreementType = AgreementType.BespokeCustomer;
            break;
          case 'bs':
            this.reqAgreementType = AgreementType.BespokeSupplier;
            break;
          case 'ps':
            this.reqAgreementType = AgreementType.PreferredSupplier;
            break;
          default:
            if (this.orgTypeId == 3) {
              this.reqAgreementType = AgreementType.GenericSupplierTerms;
            }
            break;
        }

        this.reqAppliedOrgId = params['orgId'];
        this.reqAppliedDeptId = params['deptId'];

        this.load();
      });
    });
  }

  load() {
    window.scrollTo(0, 0);
    //(this.tabviewMain as TabView).activeIndex = 0;
    //todo: set the first tab for each of services lists

    this.agreementService
      .getByType(this.reqAgreementType, this.reqAppliedOrgId, this.reqAppliedDeptId)
      .then(async (res) => {
        await this.setAgreementData(res);
        if (this.agreementData.ratecardPresetId) {
          this.initRateCardPresetValue();
        }

        //----
        this.isLoaded = true;
        this.initForm(res);
      });
  }

  private initRateCardPresetValue() {
    this.selectedRatecardPresetName = this.agreementData.ratecardPreset?.presetName;
    this.selectedRateCardPresetId = this.agreementData.ratecardPresetId;
    this.rateGridDataJson = JSON.stringify(this.rateGridData);
  }

  isDriver() {
    return this.supplierSignupType == SupplierSignupTypeEnum.Driver;
  }

  isTransporter(): boolean {
    return this.supplierSignupType == SupplierSignupTypeEnum.Agent;
  }

  isRecoverer(): boolean {
    return this.supplierSignupType == SupplierSignupTypeEnum.Agent;
  }

  async setAgreementData(res: IAgreement) {
    this.agreementData = res;

    this.access = res.dataAccess;

    console.log('RESX ', res);

    if (this.reqAppliedOrgId) {
      await this.organisationService.getOrg(this.reqAppliedOrgId).then((result) => {
        this.orgName = result.name;
        this.supplierSignupType = result.supplierSignupTypeId;
      });
      if (this.reqAppliedDeptId) {
        await this.organisationService.getDepartment(this.reqAppliedDeptId).then((res) => {
          this.deptName = res.name;
        });
      }
    } else {
      await this.organisationService
        .getOrg(this.userService.currentUser.organisationId)
        .then((result) => {
          this.orgName = result.name;
          this.supplierSignupType = result.supplierSignupTypeId;
        });
    }

    //header
    switch (res.typeId) {
      case AgreementType.GenericCustomerTerms:
        this.agreementTypeHeader = 'Generic Agreement';
        break; //shown for VME admin
      case AgreementType.GenericSupplierTerms:
        this.agreementTypeHeader = `${
          this.supplierSignupType
            ? SupplierSignupTypeEnum[this.supplierSignupType] + ' Service Agreement'
            : 'Generic Supplier Agreement'
        }`;
        break; //shown for VME admin
      case AgreementType.GenericCustomer:
        this.agreementTypeHeader = 'Generic Agreement';
        break;
      case AgreementType.GenericSupplier:
        this.agreementTypeHeader = `${
          this.supplierSignupType
            ? SupplierSignupTypeEnum[this.supplierSignupType] + ' Service Agreement'
            : 'Generic Agreement'
        }`;
        break;
      case AgreementType.BespokeCustomer:
        this.agreementTypeHeader = `Bespoke Agreement  (${this.orgName}) ${
          this.deptName ? this.deptName : ''
        }`;
        break; //shown for VME admin / bespoke customer
      case AgreementType.BespokeSupplier:
        this.agreementTypeHeader = `Bespoke Agreement  (${this.orgName})`;
        break; //shown for VME admin / bespoke supplier
      case AgreementType.PreferredSupplier:
        this.agreementTypeHeader = 'Preferred Supplier Agreement';
        break; //shown for VME admin / customer
    }

    this.serviceDefaultDesc =
      'Your agreement is preset with driving services which can be offered by any fully licensed driver.';
    if (this.supplierSignupType == SupplierSignupTypeEnum.Agent) {
      this.serviceDefaultDesc =
        'Your agreement is preset with driving services which can be offered by a basic transporter agent.';
    }

    //---- agreement tab ----
    var cardtypeCount = 5; // non-urgent, urgent and out-of-hours
    var hourstypeCount = 6; // doubled the count as we now have rate cards for postcode-based pricing

    console.log('RES ', res.allServices);

    if (this.access.servicesProvided) {
      var arrS = [];
      for (var i = 0; i < cardtypeCount; i++) arrS.push([]);

      for (var i = 0; i < res.allServices.length; i++)
        arrS[res.allServices[i].rateCardType - 1].push(res.allServices[i]);
      this.srvGridData = arrS;
      for (var i = 0; i < cardtypeCount; i++)
        this.srvGridChecked.push(this.isServiceGridChecked(i));
    }

    //---- rate card tab ----
    if (this.access.rateCard) {
      var arrR = [];
      for (var i = 0; i < cardtypeCount; i++) {
        arrR.push([]);
        for (var j = 0; j < hourstypeCount; j++) arrR[i].push([]);
      }

      for (var i = 0; i < res.ratecards.length; i++)
        arrR[res.ratecards[i].serviceCard.rateCardType - 1][
          res.ratecards[i].serviceCard.pricingHoursTypeId - 1
        ].push(res.ratecards[i]);

      //console.log("RATE CARD DATA: ", arrR);
      this.rateGridData = arrR;

      console.log('RGD ', this.rateGridData);

      this.sortBandings(res.pricing.bandings);
      this.bandings = res.pricing.bandings;
      if (!this.bandings) this.bandings = [];
    }
  }

  initForm(data: IAgreement) {
    var d: IAgreement;
    if (data.id == 0) {
      //new agreement
      d = {
        id: 0,
        typeId: 0,
        organisationId: 0,
        isAgreed: true,
        allServices: null,
        terms: {
          times: null,
          details: {
            serviceResponseTime: 15,
            serviceJobsAccepted: 95,
            pricingCancellationFee: 25,
            pricingAbortedJobFee: 15,
            pricingBankHolidayFee: 0,
            pricingLondonPremium: 0,
            pricingMargin: 10,
            waitingTimeThreshold: 0,
            chargingTimeThreshold: 0,
            cleaningServiceFee:
              this.reqAgreementType == AgreementType.GenericCustomer ||
              this.reqAgreementType == AgreementType.BespokeCustomer
                ? 5
                : 3.75,
            urgentSpecialDays: false,
            discount2Vehicles: 0,
            discount3Vehicles: 0,
            discount4Vehicles: 0,
            discount5Vehicles: 0,
            discount6Vehicles: 0,
            discount7Vehicles: 0,
            //discountReturn: null,
            discountReturn: 0,

            urgentBookBefore: '09:00',
            urgentStartAfter: '12:00',
            supplierPricing_BuyItNowPrice: 0.73,
            supplierPricing_BuyItNowPrice_Autocombine: 0.65,
            supplierPricing_TransportedBuyItNowPrice: 1.0,
          },
        },
        pricing: {
          bandings: null,
          usePriceBanding: false,
        },
        ratecards: null,
        ratecardPresetId: null,
        markupPercentage: null,
        dataAccess: null,
      };
    } else {
      //existent agreement
      d = data;
      console.log('Agreement details: ', d);
    }

    this.agreement = {
      id: d.id,
      isAgreed: new FormControl(
        d.isAgreed,
        this.access.agree == this.AccessNotShown ? null : Validators.requiredTrue,
      ),
      //agreement tab
      times: this.officeHoursService.setOfficeHoursForm(d.terms.times),
      details: {
        serviceResponseTime: new FormControl(
          d.terms.details.serviceResponseTime,
          Validators.required,
        ),
        serviceJobsAccepted: new FormControl(
          d.terms.details.serviceJobsAccepted,
          Validators.required,
        ),
        pricingCancellationFee: new FormControl(
          d.terms.details.pricingCancellationFee,
          Validators.required,
        ),
        pricingAbortedJobFee: new FormControl(
          d.terms.details.pricingAbortedJobFee,
          Validators.required,
        ),
        pricingBankHolidayFee: new FormControl(
          d.terms.details.pricingBankHolidayFee,
          Validators.required,
        ),
        pricingLondonPremium: new FormControl(
          d.terms.details.pricingLondonPremium,
          Validators.required,
        ),
        supplierPricing_BuyItNowPrice: new FormControl(
          d.terms.details.supplierPricing_BuyItNowPrice,
          Validators.required,
        ),
        supplierPricing_TransportedBuyItNowPrice: new FormControl(
          d.terms.details.supplierPricing_TransportedBuyItNowPrice,
          Validators.required,
        ),
        supplierPricing_BuyItNowPrice_Autocombine: new FormControl(
          d.terms.details.supplierPricing_BuyItNowPrice_Autocombine,
          Validators.required,
        ),
        pricingMargin: new FormControl(d.terms.details.pricingMargin, Validators.required),
        chargingTimeThreshold: new FormControl(
          d.terms.details.chargingTimeThreshold,
          Validators.required,
        ),
        waitingTimeThreshold: new FormControl(
          d.terms.details.waitingTimeThreshold,
          Validators.required,
        ),
        discount2Vehicles: new FormControl(d.terms.details.discount2Vehicles, Validators.required),
        discount3Vehicles: new FormControl(d.terms.details.discount3Vehicles, Validators.required),
        discount4Vehicles: new FormControl(d.terms.details.discount4Vehicles, Validators.required),
        discount5Vehicles: new FormControl(d.terms.details.discount5Vehicles, Validators.required),
        discount6Vehicles: new FormControl(d.terms.details.discount6Vehicles, Validators.required),
        discount7Vehicles: new FormControl(d.terms.details.discount7Vehicles, Validators.required),
        discountReturn: new FormControl(d.terms.details.discountReturn, Validators.required),
        urgentBookBefore: new FormControl(
          new Date('1970-01-01T' + d.terms.details.urgentBookBefore),
          Validators.required,
        ),
        urgentStartAfter: new FormControl(
          new Date('1970-01-01T' + d.terms.details.urgentStartAfter),
          Validators.required,
        ),
        cleaningServiceFee: new FormControl(
          d.terms.details.cleaningServiceFee,
          Validators.required,
        ),
        urgentSpecialDays: new FormControl(d.terms.details.urgentSpecialDays),
        markupPercentage: new FormControl(d.markupPercentage),
      },
      //rate card tab
      usePriceBanding: new FormControl(d.pricing.usePriceBanding),
    };

    let a = this.agreement;

    //--
    this.agreementForm = new UntypedFormGroup({
      isAgreed: a.isAgreed,
      dt1: new FormControl(),

      times: new UntypedFormGroup({
        weekOfficeOpeningTime: a.times.weekOfficeOpeningTime,
        weekOfficeClosingTime: a.times.weekOfficeClosingTime,
        saturdayOpeningTime: a.times.saturdayOpeningTime,
        saturdayClosingTime: a.times.saturdayClosingTime,
        sundayOpeningTime: a.times.sundayOpeningTime,
        sundayClosingTime: a.times.sundayClosingTime,
        weekendOfficeOpeningTime: a.times.weekendOfficeOpeningTime,
        weekendOfficeClosingTime: a.times.weekendOfficeClosingTime,
        openOnSaturday: a.times.openOnSaturday,
        openOnSunday: a.times.openOnSunday,
      }),
      details: new UntypedFormGroup({
        serviceResponseTime: a.details.serviceResponseTime,
        serviceJobsAccepted: a.details.serviceJobsAccepted,
        pricingCancellationFee: a.details.pricingCancellationFee,
        pricingAbortedJobFee: a.details.pricingAbortedJobFee,
        pricingBankHolidayFee: a.details.pricingBankHolidayFee,
        pricingLondonPremium: a.details.pricingLondonPremium,
        supplierPricing_BuyItNowPrice: a.details.supplierPricing_BuyItNowPrice,
        supplierPricing_TransportedBuyItNowPrice:
          a.details.supplierPricing_TransportedBuyItNowPrice,
        supplierPricing_BuyItNowPrice_Autocombine:
          a.details.supplierPricing_BuyItNowPrice_Autocombine,
        pricingMargin: a.details.pricingMargin,
        chargingTimeThreshold: a.details.chargingTimeThreshold,
        waitingTimeThreshold: a.details.waitingTimeThreshold,
        discount2Vehicles: a.details.discount2Vehicles,
        discount3Vehicles: a.details.discount3Vehicles,
        discount4Vehicles: a.details.discount4Vehicles,
        discount5Vehicles: a.details.discount5Vehicles,
        discount6Vehicles: a.details.discount6Vehicles,
        discount7Vehicles: a.details.discount7Vehicles,
        discountReturn: a.details.discountReturn,
        urgentBookBefore: a.details.urgentBookBefore,
        urgentStartAfter: a.details.urgentStartAfter,
        cleaningServiceFee: a.details.cleaningServiceFee,
        urgentSpecialDays: a.details.urgentSpecialDays,
        markupPercentage: a.details.markupPercentage,
      }),
      usePriceBanding: a.usePriceBanding,
    });
  }

  //todo: use MessageService ts class

  isServiceGridChecked(index) {
    for (var i = 0; i < this.srvGridData[index].length; i++)
      if (this.srvGridData[index][i].isServiceOffered) return true;
    return false;
  }

  isFixPriceGridChecked(index) {
    for (var i = 0; i < this.srvGridData[index].length; i++)
      if (this.srvGridData[index][i].isFixPrice) return true;
    return false;
  }

  changeIsServiceOffered(e, data) {
    var isChecked = e;
    var rateType = data.rateCardType;

    //update the value in services data
    var arrS = this.srvGridData[rateType - 1];
    for (var i = 0; i < arrS.length; i++) {
      if (arrS[i].serviceId == data.serviceId) {
        arrS[i].isServiceOffered = isChecked;
        if (!isChecked) {
          arrS[i].isFixPrice = isChecked;
          arrS[i].urgencyHours = null;
        }
        break;
      }
    }

    this.srvGridChecked[rateType - 1] = this.isServiceGridChecked(rateType - 1);

    if (this.access.rateCard) {
      //update the value in services data
      var arrR = this.rateGridData[rateType - 1];
      for (var i = 0; i < arrR.length; i++) {
        for (var j = 0; j < arrR[i].length; j++) {
          if (arrR[i][j].serviceCard.serviceId == data.serviceId) {
            arrR[i][j].isServiceOffered = isChecked;
            if (!isChecked) {
              arrR[i][j].isFixPrice = isChecked;
              arrR[i][j].urgencyHours = null;
            }
            break;
          }
        }
      }
    }
  }

  changeUrgencyHours(e, data) {
    var rateType = data.rateCardType;

    //update the value in services data
    var arrS = this.srvGridData[rateType - 1];
    for (var i = 0; i < arrS.length; i++) {
      if (arrS[i].serviceId == data.serviceId) {
        arrS[i].urgencyHours = e;
        break;
      }
    }

    this.srvGridChecked[rateType - 1] = this.isServiceGridChecked(rateType - 1);

    if (this.access.rateCard) {
      //update the value in services data
      var arrR = this.rateGridData[rateType - 1];
      for (var i = 0; i < arrR.length; i++) {
        for (var j = 0; j < arrR[i].length; j++) {
          if (arrR[i][j].serviceCard.serviceId == data.serviceId) {
            arrR[i][j].urgencyHours = e;
            break;
          }
        }
      }
    }
  }

  changeIsFixPrice(e, data) {
    var isChecked = e;
    var rateType = data.rateCardType;

    //update the value in services data
    var arrS = this.srvGridData[rateType - 1];
    for (var i = 0; i < arrS.length; i++) {
      if (arrS[i].serviceId == data.serviceId) {
        arrS[i].isFixPrice = isChecked;
        if (isChecked) {
          arrS[i].isServiceOffered = isChecked;
        }
        break;
      }
    }

    if (this.access.rateCard) {
      //update the value in services data
      var arrR = this.rateGridData[rateType - 1];
      for (var i = 0; i < arrR.length; i++) {
        for (var j = 0; j < arrR[i].length; j++) {
          if (arrR[i][j].serviceCard.serviceId == data.serviceId) {
            arrR[i][j].isFixPrice = isChecked;
            if (isChecked) {
              arrR[i][j].isServiceOffered = isChecked;
            }
            break;
          }
        }
      }
    }

    // if fixed price is checked, default all price mods to false
    if (isChecked) {
      this.setPremium(data, 0);
    } else {
      // if unchecked, default them all to true
      const allFlags = Object.values(ApplyPremiumFlagEnum)
        .filter((k) => !isNaN(Number(k)))
        .reduce((x, y) => Number(x) + Number(y));

      this.setPremium(data, Number(allFlags));
    }

    console.log('DATA ', data);
  }

  getServiceRowStyle(data, index) {
    return data.isServiceOffered ? 'service-offered' : 'no-service-offered';
  }

  addBanding() {
    let idGen = 0;
    this.bandings.forEach((o) => {
      if (o.id < idGen) idGen = o.id;
    });

    this.dlgBandingIndex = this.bandings.length;
    const previousBanding = this.bandings[this.dlgBandingIndex - 1];

    const previousBandingFrom = previousBanding ? previousBanding.from : -1;

    this.dlgBanding = { id: idGen - 1, from: previousBandingFrom + 1 };

    this.bandingDialogVisible = true;
  }

  editBanding(b: IAgreementPricingBanding) {
    this.dlgBanding = { id: b.id, from: b.from };
    this.dlgBandingIndex = this.bandings.findIndex((x) => x.id == b.id);
    this.bandingDialogVisible = true;
  }

  sortBandings(b: IAgreementPricingBanding[]) {
    if (!b) return;
    b.sort((a, b) => {
      return a.from > b.from ? 1 : -1;
    });
  }

  deleteBanding(b: IAgreementPricingBanding) {
    this.bandings = this.bandings.filter((o) => o != b);
    this.selectedBanding = null;

    //remove the banding from rate card's price bandings - to correctly show the Price banding dependend cells
    for (var i = 0; i < this.rateGridData.length; i++)
      for (var j = 0; j < this.rateGridData[i].length; j++)
        for (var k = 0; k < this.rateGridData[i][j].length; k++)
          if (this.rateGridData[i][j][k].pricing.bandings.length) {
            this.rateGridData[i][j][k].pricing.bandings = this.rateGridData[i][j][
              k
            ].pricing.bandings.filter((o) => o.agreementPricingBandingId != b.id);
          }
  }

  dlgSaveBanding() {
    this.bandingDialogVisible = false;
    const banding = this.bandings.find((o) => o.id == this.dlgBanding.id);
    if (banding == null) {
      this.bandings.push(this.dlgBanding);
      this.sortBandings(this.bandings);
    } else {
      banding.from = this.dlgBanding.from;
    }

    this.bandings = [...this.bandings]; //to update the grid
  }

  bandingValid(b: IAgreementPricingBanding) {
    if (!this.bandingDialogVisible) {
      return [true, ''];
    }

    const previousBand = this.bandings[this.dlgBandingIndex - 1];
    const nextBand = this.bandings[this.dlgBandingIndex + 1];

    if (this.dlgBandingIndex == 0) {
      return [b.from == 0, 'First banding must start from 0 miles.'];
    }

    if (!nextBand) {
      return [
        b.from > previousBand.from,
        `Value must be greater than previous bands value (${previousBand.from})`,
      ];
    }

    if (b.from <= previousBand.from) {
      return [false, `Value must be greater than previous bands value (${previousBand.from})`];
    }

    if (b.from >= nextBand.from) {
      return [false, `Value must be less than next bands value (${nextBand.from})`];
    }

    return [true, ''];
  }

  serviceNotAccessible() {
    return (
      this.access.servicesProvided == this.AccessReadOnly ||
      (this.access.servicesProvided == this.AccessReadOnlyUntilAgreed &&
        this.agreementData.isAgreed)
    );
  }

  customValidateOnSave(data: IAgreement) {
    //validate selected services
    if (
      this.access.servicesProvided == this.AccessEditable ||
      (!data.isAgreed && this.access.servicesProvided == this.AccessReadOnlyUntilAgreed)
    ) {
      if (data.allServices.find((o) => o.isServiceOffered) == null) {
        this.messageService.add(Message.Error('You must select the services to offer.', 'Error'));
        return false;
      }
    }

    if (this.access.rateCard == this.AccessEditable) {
      //validate rate card prices (excluding bandings)
      // #989 allow zero prices but not negative
      var hasZeroPrices =
        data.ratecards.find((o) => {
          var m = (o as any).serviceCard.mask;
          var p = o.pricing;
          return (
            o.isServiceOffered &&
            !(
              (m.callOut && p.callOut >= 0) ||
              (m.perVehicle && p.perVehicle >= 0) ||
              (m.ratePerHr && p.ratePerHr >= 0) ||
              (m.perJob && p.perJob >= 0) ||
              //OR perMile valus is not set && price bandings (if used) have no values set
              (m.perMile &&
                (p.perMile >= 0 ||
                  (data.pricing.usePriceBanding && p.bandings.find((b) => b.price >= 0) != null)))
            )
          );
        }) != null;

      if (hasZeroPrices) {
        this.messageService.add(
          Message.Error('Please enter the rate card prices for your selected services.', 'Error'),
        );
        return false;
      }

      let hasBadBands =
        data.ratecards.find((o) => {
          let p = o.pricing;
          return (
            o.isServiceOffered &&
            ((p.band2LowerMileageThreshold != 0 &&
              p.band2LowerMileageThreshold < p.includedMiles) ||
              (p.band3LowerMileage != 0 &&
                (p.band3LowerMileage < p.band2LowerMileageThreshold ||
                  p.band3LowerMileage < p.includedMiles)))
          );
        }) != null;

      if (hasBadBands) {
        this.messageService.add(Message.Error('Please correct band values.', 'Error'));
        return false;
      }

      //validate rate card prices bandings
      if (data.pricing.usePriceBanding) {
        const invalidServiceBandings = data.ratecards.map((o) => {
          const serviceCard = (o as any).serviceCard;
          const p = o.pricing;
          if (!o.isServiceOffered || p.bandings.length == data.pricing.bandings.length) {
            return { valid: true, serviceCard };
          }
          return {
            valid: false,
            serviceCard,
          };
        });

        const firstInvalidBanding = invalidServiceBandings.filter((x) => x.valid == false)[0];
        const firstInvalidServiceCard = firstInvalidBanding?.serviceCard;

        if (firstInvalidServiceCard) {
          const serviceDescription = firstInvalidServiceCard.serviceTypeName;
          const minWeight = firstInvalidServiceCard.minWeight;
          const maxWeight = firstInvalidServiceCard.maxWeight;

          let weightDescription = '';

          if (minWeight == null && maxWeight != null) {
            weightDescription = ` (< ${maxWeight}T) `;
          } else if (minWeight != null && maxWeight == null) {
            weightDescription = ` (> ${minWeight}T) `;
          } else if (minWeight != null && maxWeight != null) {
            weightDescription = ` (${minWeight}T - ${maxWeight}T) `;
          }

          let pricingHoursTypeName = pricingHoursTypeNames.get(
            firstInvalidServiceCard.pricingHoursTypeId,
          );

          const pricingHoursDescription = pricingHoursTypeName
            ? `, Pricing Type: ${pricingHoursTypeName}`
            : '';

          const errorMsg = `'Price Band' values missing for service ${serviceDescription}${weightDescription}${pricingHoursDescription}`;

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMsg,
            life: 5000,
          });

          return false;
        }

        if (firstInvalidBanding) {
          this.messageService.add(
            Message.Error(
              "Please enter the rate card prices in the 'Price Band' column for the distance bandings you have set up.",
              'Error',
            ),
          );
          return false;
        }
      }
    }

    return true;
  }

  save(formValues) {
    ///time fixes
    formValues.times = this.officeHoursService.fixHoursFormatForSave(formValues.times);
    formValues.details.urgentBookBefore = this.officeHoursService.fixTimeForSave(
      formValues.details.urgentBookBefore,
    );
    formValues.details.urgentStartAfter = this.officeHoursService.fixTimeForSave(
      formValues.details.urgentStartAfter,
    );

    ///
    var allServices = [];

    if (
      this.access.servicesProvided == this.AccessEditable ||
      ((this.agreementData.isAgreed || formValues.isAgreed) &&
        this.access.servicesProvided == this.AccessReadOnlyUntilAgreed)
    ) {
      for (var i = 0; i < this.srvGridData.length; i++)
        allServices = allServices.concat(this.srvGridData[i]);
    }

    ///rate cards
    var ratecards: IRateCard[] = [];

    if (this.access.rateCard == this.AccessEditable) {
      //console.log("SETTING RATE CARD DATA: ", this.rateGridData)

      for (var i = 0; i < this.rateGridData.length; i++)
        for (var j = 0; j < this.rateGridData[i].length; j++)
          ratecards = ratecards.concat(this.rateGridData[i][j]);

      ratecards = ratecards.filter((o) => o.isServiceOffered);
    }

    //console.log('Saving ratecards: ', ratecards);

    //Check if the rate card is the same
    var isDifferent = false;
    if (this.selectedRateCardPresetId) {
      var currentRateCardJson = JSON.stringify(this.rateGridData);
      isDifferent = this.rateGridDataJson !== currentRateCardJson;
      if (isDifferent) {
        this.selectedRateCardPresetId = undefined;
        this.selectedRatecardPresetName = undefined;
      }
    }
    ///
    var data: IAgreement;
    data = {
      id: this.agreementData.id,
      typeId: this.agreementData.typeId,
      organisationId: this.reqAppliedOrgId,
      departmentId: this.reqAppliedDeptId,
      isAgreed: formValues.isAgreed,
      allServices: allServices,
      terms: {
        details: formValues.details,
        times: formValues.times,
      },
      pricing: {
        usePriceBanding: formValues.usePriceBanding,
        bandings: this.bandings,
      },
      ratecards: ratecards,
      ratecardPresetId: !isDifferent ? this.selectedRateCardPresetId : undefined,
      markupPercentage: formValues.details.markupPercentage,
      dataAccess: null, //this was never use, but to make the complier happy, i have to pass it in
    };

    //custom validation
    if (!this.customValidateOnSave(data)) return;

    this.isSaved = true;
    console.log('Agreement will save: ', data);
    //return;

    //saving
    this.agreementService
      .save(data)
      .then((res) => {
        this.setAgreementData(res);

        if (!res.errorMsg) {
          this.messageService.add(Message.Success('Agreement saved', 'Saving'));
        } else {
          this.messageService.add(Message.Error(res.errorMsg, 'Error'));
        }
      })
      .catch((res) => this.messageService.add(Message.Error('Details not saved', 'Error')));
  }

  changeApplyPremium(e: any, data: any, premiumFlag: number) {
    var isChecked = e;
    var rateType = data.rateCardType;

    //update the value in services data
    var arrS = this.srvGridData[rateType - 1];
    for (var i = 0; i < arrS.length; i++) {
      if (arrS[i].serviceId == data.serviceId) {
        if (isChecked) {
          arrS[i].applyPremium = arrS[i].applyPremium | premiumFlag;
        } else {
          arrS[i].applyPremium = arrS[i].applyPremium & ~premiumFlag;
        }

        //console.log(`arrS[${i}] = ${arrS[i].applyPremium}, premium flag = ${premiumFlag}`);

        if (isChecked) {
          arrS[i].isServiceOffered = isChecked;
        }
        break;
      }
    }

    if (this.access.rateCard) {
      //update the value in services data
      var arrR = this.rateGridData[rateType - 1];
      for (var i = 0; i < arrR.length; i++) {
        for (var j = 0; j < arrR[i].length; j++) {
          if (arrR[i][j].serviceCard.serviceId == data.serviceId) {
            if (isChecked) {
              arrR[i][j].applyPremium = arrR[i][j].applyPremium | premiumFlag;
            } else {
              arrR[i][j].applyPremium = arrR[i][j].applyPremium & ~premiumFlag;
            }

            //console.log(`arrR[${i}][${j}] = ${arrR[i][j].applyPremium}, premium flag = ${premiumFlag}`);

            if (isChecked) {
              arrR[i][j].isServiceOffered = isChecked;
            }
            break;
          }
        }
      }
    }
  }

  setPremium(data, premium) {
    var rateType = data.rateCardType;

    var arrS = this.srvGridData[rateType - 1];
    for (var i = 0; i < arrS.length; i++) {
      if (arrS[i].serviceId == data.serviceId) {
        arrS[i].applyPremium = premium;
        break;
      }
    }

    if (this.access.rateCard) {
      //update the value in services data
      var arrR = this.rateGridData[rateType - 1];
      for (var i = 0; i < arrR.length; i++) {
        for (var j = 0; j < arrR[i].length; j++) {
          if (arrR[i][j].serviceCard.serviceId == data.serviceId) {
            arrR[i][j].applyPremium = premium;
            break;
          }
        }
      }
    }
  }

  onPresetSelected(event: { id: number; data: any; presetName: string }) {
    // set the preset id so it can be saved with the agreement
    this.selectedRateCardPresetId = event.id;
    this.selectedRatecardPresetName = event.presetName;

    // set the current ratecard data from the preset
    // we need to match the current ratecard identities for service cards etc.
    // so that they have no connection with the preset
    // copy the pricing data for each existing rate-card from the equivalent rate-card in the preset (event.data)

    // set serviceOffered to true for all ratecard entries
    let arrR = this.rateGridData;
    for (let i = 0; i < arrR.length; i++) {
      for (let j = 0; j < arrR[i].length; j++) {
        for (let k = 0; k < arrR[i][j].length; k++) {
          arrR[i][j][k].isServiceOffered = true;

          // set service offered to true in the agreement for all service cards
          this.srvGridChecked[arrR[i][j][k].serviceCard.rateCardType - 1] = true;
        }
      }
    }

    // clear existing price data for all ratecard entries
    this.rateGridData.forEach((r, index) => {
      r.forEach((rateCard, rIndex) => {
        rateCard.forEach((p) => {
          this.resetPriceProperties(p.pricing);
          p.isServiceOffered = false;
        });
      });
    });

    // set service grid data for appropriate services (i.e. where any pricing values are set)
    event.data.forEach((d, index) => {
      //console.log(`** CHECKING RATES ${index}: `, d);

      d.forEach((rateCard, rIndex) => {
        //console.log(`|| RATES ${rIndex}: `, rateCard);

        rateCard.forEach((p) => {
          const allValues = Object.entries(p.pricing)
            .filter(
              ([key, value]) =>
                key !== 'servicePricingId' && !Array.isArray(value) && !isNaN(Number(value)),
            )
            .reduce((acc, [, value]) => acc + Number(value), 0);

          if (allValues > 0) {
            let serviceId = p.serviceCard.serviceId;

            // find the equivalent serviceIds in current rate data
            let rate = this.rateGridData[index][rIndex].find(
              (x) => x.serviceCard.serviceId == serviceId,
            );

            if (rate) {
              let tempId = rate.pricing.servicePricingId;
              rate.pricing = deepClone(p.pricing);
              rate.pricing.servicePricingId = tempId;
              rate.isServiceOffered = true;
            }

            let srv = this.srvGridData[index].find((x) => x.serviceId == serviceId);
            if (srv) {
              srv.isServiceOffered = true;
            }
          }
        });
      });
    });
    this.rateGridDataJson = JSON.stringify(this.rateGridData);
  }

  resetPriceProperties(obj) {
    for (const key in obj) {
      // Check if the property is not an array and does not have the name 'servicePricingId'
      if (!Array.isArray(obj[key]) && key !== 'servicePricingId') {
        obj[key] = 0;
      }
    }
  }

  onUsePriceBandingChange(usePriceBanding: boolean) {
    if (usePriceBanding) {
      if (this.bandings.length == 0) {
        // this.bandings.push({ id: 0, from: 0 });
      }
    }
  }

  loadRateCardInclusivityTypes(): void {
    this.agreementService.getInclusivityTypes().then((result) => {
      this.inclusivityTypes = result;
    });
  }
}
